import * as yup from 'yup';

import {
  DAY_NUMBER_MAPPING,
  DAY_NUMBER_MAPPING_REVERSE,
} from 'features/hiringV2/constants';
import {
  JobRequest,
  JobRequestWeekAvailability,
  JobSyndicationSettings,
} from 'features/hiringV2/interfaces/IJobRequest';

import { toI18n } from 'util/i18n';
import { updateQuery } from 'util/router';

import {
  initialJobData,
  initialPartnerToggles,
  MINIMUM_SCREENER_QUESTIONS,
  STAGE_NUMBER_MAPPING,
} from './constants';
import { JobApplicationQuestion } from './interfaces';
import {
  JobData,
  JobDataErrors,
  JobFormStage,
  JobFormStep,
  JobPay,
  JobRequirements,
  JobRequirementsArray,
  StageToSteps,
} from './types';

// Validations
export const jobDescriptionSchema = yup.object().shape({
  job_role: yup.string().required('Job role is required'),
  job_requirements: yup.object().shape({
    type: yup
      .string()
      .required(toI18n('hiring.v2.job_request.errors.job_requirements.type')),
    days: yup
      .array()
      .of(yup.string())
      .required(toI18n('hiring.v2.job_request.errors.job_requirements.days')),
    shifts: yup
      .array()
      .of(yup.string())
      .required(toI18n('hiring.v2.job_request.errors.job_requirements.shifts')),
    years_of_experience: yup
      .string()
      .required(
        toI18n(
          'hiring.v2.job_request.errors.job_requirements.years_of_experience'
        )
      ),
  }),
  job_description: yup
    .string()
    .required(toI18n('hiring.v2.job_request.errors.job_description.short')),
  job_pay: yup.object().shape({
    range: yup
      .object()
      .shape({
        min: yup
          .number()
          .required(toI18n('hiring.v2.job_request.errors.job_pay.range'))
          .min(1, toI18n('hiring.v2.job_request.errors.job_pay.min')),
        max: yup.number(),
        frequency: yup
          .string()
          .required(toI18n('hiring.v2.job_request.errors.job_pay.frequency')),
      })
      .required(toI18n('hiring.v2.job_request.errors.job_pay.range'))
      .test(
        'valid-range',
        toI18n('hiring.v2.job_request.errors.job_pay.min_max'),
        value => {
          if (!value || !value.max) return true; // Let the required validation handle empty values
          return value.min <= value.max;
        }
      ),
  }),
});

export type JobDescriptionValidStep =
  | 'job_role'
  | 'job_requirements'
  | 'job_pay';

export const validateJobDataDescription = (jobData: JobData) => {
  const stepSchemas: Record<JobDescriptionValidStep, yup.Schema<any>> = {
    job_role: yup.object().shape({
      job_role: jobDescriptionSchema.fields?.job_role as yup.Schema<string>,
    }),
    job_requirements: yup.object().shape({
      job_requirements: jobDescriptionSchema.fields
        ?.job_requirements as yup.Schema<JobRequirements>,
    }),
    job_pay: yup.object().shape({
      job_pay: jobDescriptionSchema.fields?.job_pay as yup.Schema<JobPay>,
    }),
  };

  let errors: Record<string, string> | undefined;

  // Validate all steps and track validity
  const validObject: Record<JobDescriptionValidStep, boolean> = {
    job_role: false,
    job_requirements: false,
    job_pay: false,
  };

  (Object.keys(stepSchemas) as JobDescriptionValidStep[]).forEach(stepKey => {
    try {
      stepSchemas[stepKey].validateSync(jobData, { abortEarly: false });
      validObject[stepKey] = true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        errors = err.inner.reduce((acc, error) => {
          if (error.path) acc[error.path] = error.message;
          return acc;
        }, {} as Record<string, string>);
      }
      validObject[stepKey] = false;
    }
  });

  const areAllStepsValid = Object.values(validObject).every(Boolean);

  return { areAllStepsValid, errors, validObject };
};

export const formatJobFormItemError = (
  error: string | string[] | undefined
) => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error.join(', ');
  return '';
};

export const formatJobPayError = (errors: JobDataErrors['job_pay']) => {
  if (errors?.range?.min) {
    return formatJobFormItemError(errors?.range?.min);
  }
  if (errors?.range?.frequency) {
    return formatJobFormItemError(errors?.range?.frequency);
  }
  if (typeof errors?.range === 'string' || Array.isArray(errors?.range)) {
    return formatJobFormItemError(errors?.range);
  }
  return '';
};

export const isScreenerStepComplete = (
  applicationQuestions: JobApplicationQuestion[] | undefined
) =>
  !!applicationQuestions &&
  applicationQuestions.length >= MINIMUM_SCREENER_QUESTIONS;

export const isJobDetailStepComplete = (
  id: string,
  errors: JobDataErrors | undefined
) => {
  // Each accordion is dependent on the previous one
  const job_role_completed = !errors?.job_role;
  const job_requirements_completed =
    job_role_completed && !errors?.job_requirements;
  const job_pay_completed = job_requirements_completed && !errors?.job_pay;
  const more_info_completed = job_pay_completed && !errors?.more_info;

  switch (id) {
    case 'job_role':
      return job_role_completed;
    case 'job_requirements':
      return job_requirements_completed;
    case 'job_pay':
      return job_pay_completed;
    case 'more_info':
      return more_info_completed;
    default:
      return false;
  }
};

export const goToNextStage = <S extends JobFormStage>(
  stage: S,
  step?: StageToSteps[S]
) => {
  updateQuery(`stage`, stage);
  updateQuery(`step`, step);
};

export const mapJobDataDaysAndShiftsToWeekAvailability = (
  days: string[],
  shifts: string[]
): JobRequestWeekAvailability => {
  const weekAvailabilities: JobRequestWeekAvailability = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };
  days.forEach(day => {
    const dayKey = DAY_NUMBER_MAPPING[day] as keyof JobRequestWeekAvailability;
    weekAvailabilities[dayKey] = shifts;
  });
  return weekAvailabilities;
};

export const getJobDataDaysFromWeekAvailability = (
  weekAvailability: JobRequestWeekAvailability | undefined
): string[] => {
  if (!weekAvailability) return [];
  return (Object.keys(weekAvailability) as (keyof JobRequestWeekAvailability)[])
    .filter(day => Array.isArray(weekAvailability[day]))
    .filter(day => !!weekAvailability[day].length)
    .map(day => DAY_NUMBER_MAPPING_REVERSE[day]);
};

export const getJobDataShiftsFromWeekAvailability = (
  weekAvailability: JobRequestWeekAvailability | undefined
): string[] => {
  if (!weekAvailability) return [];
  const firstDayWithShifts = (
    Object.keys(weekAvailability) as (keyof JobRequestWeekAvailability)[]
  ).find(day => weekAvailability[day].length > 0);
  if (!firstDayWithShifts) return [];
  return weekAvailability[firstDayWithShifts];
};

const getIfFacebookJobsEnabled = (jobData: JobData) => {
  if (!jobData.facebook_jobs_enabled) return false;
  return jobData.syndication_settings?.partner_toggles?.facebook_jobs || true;
};

const getPartnerToggles = (
  jobData: JobData
): JobSyndicationSettings['partner_toggles'] => {
  if (!jobData.syndication_settings?.partner_toggles)
    return initialPartnerToggles;
  return {
    ...jobData.syndication_settings?.partner_toggles,
    facebook_jobs: getIfFacebookJobsEnabled(jobData),
  };
};

export const mapJobDataToJobRequest = (jobData: JobData): JobRequest => {
  const jobRequest: JobRequest = {
    id: jobData.id || 0,
    role_id: jobData.role_id,
  };
  jobRequest.role_name = jobData.job_role;
  jobRequest.title = jobData.job_title || jobData.job_role;
  jobRequest.employment_type = jobData.job_requirements.type;
  jobRequest.description = jobData.job_description;
  jobRequest.years_experience = jobData.job_requirements.years_of_experience;
  jobRequest.job_compensation = {
    tip_amount: jobData.job_pay?.tips?.amount,
    tip_frequency: jobData.job_pay?.tips?.time_range,
    target_wage_rate: jobData.job_pay?.range?.min,
    target_wage_rate_max: jobData.job_pay?.range?.max || null,
    wage_type: jobData.job_pay?.range?.frequency,
    comments: jobData.job_pay?.benefits,
  };
  jobRequest.builder_progress = jobData.builder_progress;
  jobRequest.syndication_settings = {
    ...jobData.syndication_settings,
    partner_toggles: getPartnerToggles(jobData),
  };
  jobRequest.week_availabilities = mapJobDataDaysAndShiftsToWeekAvailability(
    jobData.job_requirements.days,
    jobData.job_requirements.shifts
  );
  jobRequest.application_questions = jobData.application_questions;
  jobRequest.status = jobData.status;

  return jobRequest;
};

export const mapJobRequestToJobData = (jobRequest: JobRequest): JobData => {
  const jobData = {
    id: jobRequest.id,
    status: jobRequest.status,
  } as JobData;
  jobData.role_id = jobRequest.role_id;
  jobData.job_title = jobRequest.title || jobRequest.role_name || '';
  jobData.job_role = jobRequest.role_name || '';
  jobData.job_description = jobRequest.description || '';
  jobData.prompt = jobRequest.builder_progress?.prompt_body || '';
  jobData.job_requirements = {
    type: jobRequest.employment_type || '',
    years_of_experience: jobRequest.years_experience || '',
    days: getJobDataDaysFromWeekAvailability(jobRequest.week_availabilities),
    shifts: getJobDataShiftsFromWeekAvailability(
      jobRequest.week_availabilities
    ),
  };
  jobData.job_pay = {
    range: {
      min: jobRequest.job_compensation?.target_wage_rate || 0,
      max: jobRequest.job_compensation?.target_wage_rate_max || 0,
      frequency: jobRequest.job_compensation?.wage_type || '',
    },
    tips: {
      amount: jobRequest.job_compensation?.tip_amount || 0,
      time_range: jobRequest.job_compensation?.tip_frequency || '',
    },
    benefits: jobRequest.job_compensation?.comments || '',
  };
  jobData.more_info = jobRequest.description || '';
  jobData.builder_progress = jobRequest.builder_progress;
  jobData.syndication_settings =
    jobRequest.syndication_settings || initialJobData.syndication_settings;
  jobData.facebook_jobs_enabled = jobRequest.facebook_jobs_enabled;
  return jobData;
};

export const getStageNumber = (stage: JobFormStage | undefined) =>
  stage ? STAGE_NUMBER_MAPPING[stage] : 1;

export const getStepNumber = (
  stage: JobFormStage | undefined,
  step: JobFormStep | undefined
) => {
  if (!stage) return 1;
  if (!step) return 1;

  if (stage === 'job_description') {
    if (step === 'creating') return 2;
  }
  if (stage === 'screener') {
    if (step === 'questions') return 2;
  }
  if (stage === 'review_and_post') {
    if (step === 'job_review') return 1;
    if (step === 'screener') return 2;
    if (step === 'interview_times') return 3;
    if (step === 'review_business_info') return 4;
    if (step === 'job_boards') return 5;
  }
  return 1;
};

export const getStageFromNumber = (number: number | undefined) => {
  if (!number) return 'job_description';
  const stage = Object.keys(STAGE_NUMBER_MAPPING).find(
    key => STAGE_NUMBER_MAPPING[key as JobFormStage] === number
  );
  if (!stage) return 'job_description';
  return stage as JobFormStage;
};

export const getStepFromNumber = (
  stage: JobFormStage | undefined,
  number: number | undefined
): JobFormStep | undefined => {
  if (!stage) return undefined;
  if (!number) return undefined;

  if (stage === 'job_description') {
    if (number === 2) return 'creating';
    return undefined;
  }
  if (stage === 'screener') {
    if (number === 2) return 'questions';
    return 'review';
  }
  if (stage === 'review_and_post') {
    if (number === 1) return 'job_review';
    if (number === 2) return 'screener';
    if (number === 3) return 'interview_times';
    if (number === 4) return 'review_business_info';
    if (number === 5) return 'job_boards';
    return 'job_review';
  }
  return undefined;
};

export const jobRequirementValueIsArray = (
  key: string
): key is keyof JobRequirementsArray => key === 'days' || key === 'shifts';

export const resolveAutoGenStageAndStep = (
  jobData: JobData
): { stage: JobFormStage; step: JobFormStep | undefined } => {
  const stageNumber = jobData.builder_progress?.step_number;
  const stepNumber = jobData.builder_progress?.substep_number;

  const currStage = getStageFromNumber(stageNumber);
  const currStep = getStepFromNumber(currStage, stepNumber);

  switch (currStage) {
    case 'job_description': {
      const { areAllStepsValid } = validateJobDataDescription(jobData);
      if (areAllStepsValid)
        return { stage: 'job_description', step: 'creating' };
      return { stage: currStage, step: currStep };
    }
    default:
      return { stage: currStage, step: currStep };
  }
};
